import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../../features/auth/model/auth.store';
import { useSyncStore } from '../../features/sync/model/sync.store';

export interface ProfileMenuProps {
  /**
   * bottom — меню вверх (десктоп-сайдбар снизу).
   * top — меню вниз (мобильная шапка).
   */
  dropdownDirection?: 'top' | 'bottom';
  /** Компактная кнопка (аватар) для мобильной шапки. */
  compact?: boolean;
}

export function ProfileMenu({
  dropdownDirection = 'bottom',
  compact = false,
}: ProfileMenuProps) {
  const user = useAuthStore((state) => state.user);
  const logout = useAuthStore((state) => state.logout);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  if (user === null) {
    return null;
  }

  async function handleLogout() {
    setOpen(false);
    await logout({ server: true });
    navigate('/login', { replace: true });
  }

  const dropdownClass =
    dropdownDirection === 'top'
      ? 'absolute left-0 right-0 top-full z-40 mt-2 rounded-xl border border-tg-border-soft bg-tg-surface p-2 shadow-panel'
      : 'absolute bottom-full left-0 right-0 z-40 mb-2 rounded-xl border border-tg-border-soft bg-tg-surface p-2 shadow-panel';

  return (
    <div className="relative">
      <button
        type="button"
        className={
          compact
            ? 'tg-btn-secondary flex h-10 w-10 items-center justify-center rounded-full p-0'
            : 'tg-btn-secondary w-full justify-between'
        }
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={compact ? `Меню: ${user.login}` : undefined}
        onClick={() => {
          setOpen((value) => !value);
        }}
      >
        {compact ? (
          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-tg-accent-soft text-sm font-semibold text-tg-accent">
            {user.login.slice(0, 1).toUpperCase()}
          </span>
        ) : (
          <>
            <span className="flex min-w-0 items-center gap-2">
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-tg-accent-soft text-sm font-semibold text-tg-accent">
                {user.login.slice(0, 1).toUpperCase()}
              </span>
              <span className="min-w-0 truncate text-left">
                <span className="block truncate text-sm text-tg-text">{user.login}</span>
                <span className="block text-xs text-tg-text-muted">
                  {user.isAdmin ? 'Администратор' : 'Пользователь'}
                </span>
              </span>
            </span>
            <span aria-hidden="true" className="text-tg-text-muted">
              {open ? '▴' : '▾'}
            </span>
          </>
        )}
      </button>

      {open ? (
        <div className={dropdownClass} role="menu">
          <Link
            to="/habits"
            className="tg-nav-item"
            role="menuitem"
            onClick={() => {
              setOpen(false);
            }}
          >
            Привычки
          </Link>
          <Link
            to="/projects"
            className="tg-nav-item"
            role="menuitem"
            onClick={() => {
              setOpen(false);
            }}
          >
            Проекты
          </Link>
          <Link
            to="/tags"
            className="tg-nav-item"
            role="menuitem"
            onClick={() => {
              setOpen(false);
            }}
          >
            Теги
          </Link>
          <Link
            to="/settings"
            className="tg-nav-item"
            role="menuitem"
            onClick={() => {
              setOpen(false);
            }}
          >
            Настройки
          </Link>
          {user.isAdmin ? (
            <Link
              to="/admin"
              className="tg-nav-item"
              role="menuitem"
              onClick={() => {
                setOpen(false);
              }}
            >
              Админ
            </Link>
          ) : null}
          <button
            type="button"
            className="tg-nav-item w-full text-left text-tg-danger hover:bg-tg-danger-soft"
            role="menuitem"
            onClick={() => {
              void handleLogout();
            }}
          >
            Выйти
          </button>
        </div>
      ) : null}
    </div>
  );
}

export function useLogoutNavigation() {
  const logout = useAuthStore((state) => state.logout);
  const navigate = useNavigate();
  return async () => {
    await logout({ server: true });
    navigate('/login', { replace: true });
  };
}

export function SyncFooter() {
  const online = useSyncStore((state) => state.online);
  return (
    <p className="text-xs text-tg-text-muted">
      {online ? 'Данные синхронизируются автоматически' : 'Офлайн-режим: изменения сохраняются локально'}
    </p>
  );
}
